import { IconPackage } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { MaterialTypeBadge, StockStatusBadge } from './badges.js';
import { formatQuantity } from './inventory.js';
import classes from './Materials.module.css';
import type { MaterialDetail, MaterialProvisionFields } from './types.js';

/**
 * Kumanya kaleminin katalog alanları (ADR 0099 §2): Material Master'da salt okunur; düzenleme
 * kütüphane ve Inventory ▸ Provision ekranındadır. Alerjen etiketi doğrulanmamışsa uyarı.
 */
function ProvisionFacts({ fields }: { fields: MaterialProvisionFields }) {
  const { t } = useTranslation();
  const p = (key: string, options: Record<string, unknown> = {}) => t(`provisions.${key}`, options);
  const fact = (label: string, value: ReactNode) => (
    <div>
      <dt>{label}</dt>
      <dd>{value ?? '—'}</dd>
    </div>
  );
  const tracking = (mode: string | null) =>
    mode ? p(`tracking.${mode}`, { defaultValue: mode }) : '—';
  return (
    <section className={classes.vesselStock} aria-label={t('materials.detail.provision')}>
      <h4>{t('materials.detail.provision')}</h4>
      <dl className={classes.facts}>
        {fact(p('columns.itemCode'), fields.itemCode)}
        {fact(
          p('columns.storage'),
          fields.storageType
            ? p(`storageTypes.${fields.storageType}`, { defaultValue: fields.storageType })
            : null,
        )}
        {fact(p('columns.temperature'), fields.temperatureGuidance)}
        {fact(p('columns.packSize'), fields.packSize)}
        {fact(
          p('columns.costCenter'),
          fields.costCenter
            ? p(`costCenters.${fields.costCenter}`, { defaultValue: fields.costCenter })
            : null,
        )}
        {fact(p('detail.batchTracking'), tracking(fields.batchTracking))}
        {fact(p('detail.expiryTracking'), tracking(fields.expiryTracking))}
        {fact(p('detail.fefo'), fields.fefoRequired ? p('detail.yes') : p('detail.no'))}
        {fact(
          p('columns.allergens'),
          fields.allergenTags === null
            ? p('detail.allergensUnknown')
            : fields.allergenTags.length === 0
              ? p('detail.allergensNone')
              : `${fields.allergenTags
                  .map((tag) => p(`allergens.${tag}`, { defaultValue: tag }))
                  .join(', ')}${fields.allergenVerified ? '' : ` — ${p('hints.allergenStarter')}`}`,
        )}
        {fields.dietaryTags?.length
          ? fact(p('detail.dietary'), fields.dietaryTags.join(', '))
          : null}
        {fact(t('materials.detail.librarySource'), fields.librarySource)}
        {fields.active ? null : fact(t('materials.detail.status'), p('detail.inactive'))}
      </dl>
    </section>
  );
}

export interface MaterialDetailPanelProps {
  material: MaterialDetail;
  scope: 'web' | 'ship';
  /** Ofiste geminin adı için. */
  vessels: readonly PersonnelVessel[];
  actions?: ReactNode;
}

/**
 * Malzeme kartı (`satin-alma-akisi.jpeg` "Material Detail"): ürün görseli, ad, kod, rozetler,
 * bilgi tablosu, ilgili ekipman ve notlar. Gemide dosya deposu olmadığı için fotoğraf yerine ürün
 * ikonu (M12 planı "Müşteri ekranından farklar"); ilgili ekipman PMS ile (M7) dolar. Boş isteğe
 * bağlı olgular (IMPA / ISSA, ikinci kategori) gizlenir (M6f G5-10).
 */
export function MaterialDetailPanel({
  material,
  scope,
  vessels,
  actions,
}: MaterialDetailPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`materials.detail.${key}`, options);
  const n = (value: number | null) => (value === null ? '—' : formatQuantity(value, locale));
  const own = scope === 'ship' ? (material.stock[0] ?? null) : null;
  const vesselName = (id: string) => vessels.find((v) => v.id === id)?.name ?? '—';
  const fact = (label: string, value: ReactNode) => (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );

  return (
    <Panel title={k('title')} extra={actions} aria-label={k('title')}>
      <div className={classes.detail}>
        <div className={classes.photo} aria-hidden>
          <IconPackage size={72} stroke={1.1} />
        </div>
        <div className={classes.detailMain}>
          <h3 className={classes.detailName}>{material.name}</h3>
          <span className={classes.detailCode}>{material.companyCode ?? k('noCode')}</span>
          <div className={classes.badges}>
            <StatusBadge tone="info">{material.mainCategory}</StatusBadge>
            <StatusBadge tone="info">{material.subCategory}</StatusBadge>
            <MaterialTypeBadge type={material.materialType} />
            {material.critical ? (
              <StatusBadge tone="danger">{k('criticalBadge')}</StatusBadge>
            ) : null}
          </div>
          <dl className={classes.facts}>
            {fact(k('uom'), material.unit)}
            {fact(k('specification'), material.specification ?? '—')}
            {fact(k('critical'), material.critical ? k('yes') : k('no'))}
            {material.impaCode || material.issaCode
              ? fact(k('impaIssa'), `${material.impaCode ?? '—'} / ${material.issaCode ?? '—'}`)
              : null}
            {material.secondaryMainCategory
              ? fact(
                  k('secondaryCategory'),
                  `${material.secondaryMainCategory} · ${material.secondarySubCategory ?? ''}`,
                )
              : null}
            {scope === 'ship' ? (
              <>
                {fact(k('minStock'), own ? n(own.minQty) : '—')}
                {fact(k('maxStock'), own ? n(own.maxQty) : '—')}
                {fact(
                  k('onHand'),
                  own ? (
                    <span className={classes.inline}>
                      {`${n(own.onHand)} ${own.unit}`}
                      <StockStatusBadge status={own.status} />
                    </span>
                  ) : (
                    k('notStocked')
                  ),
                )}
              </>
            ) : null}
          </dl>
          {material.provision ? <ProvisionFacts fields={material.provision} /> : null}
          {scope === 'web' ? (
            <div className={classes.vesselStock}>
              <h4>{k('vesselStock')}</h4>
              {material.stock.length === 0 ? (
                <p className={classes.muted}>{k('noVesselStock')}</p>
              ) : (
                <table className={classes.miniTable}>
                  <thead>
                    <tr>
                      <th scope="col">{k('vessel')}</th>
                      <th scope="col">{k('onHand')}</th>
                      <th scope="col">{k('minStock')}</th>
                      <th scope="col">{k('maxStock')}</th>
                      <th scope="col">{k('status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {material.stock.map((item) => (
                      <tr key={item.id}>
                        <td>{vesselName(item.vesselId)}</td>
                        <td className={classes.number}>{`${n(item.onHand)} ${item.unit}`}</td>
                        <td className={classes.number}>{n(item.minQty)}</td>
                        <td className={classes.number}>{n(item.maxQty)}</td>
                        <td>
                          <StockStatusBadge status={item.status} variant="management" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}
        </div>
        <div className={classes.detailSide}>
          <section className={classes.sideCard} aria-label={k('relatedEquipment')}>
            <h4>{k('relatedEquipment')}</h4>
            {/* Ekipman ağacı PMS ile (M7) gelir; o zamana dek nötr boş durum (G5-11) */}
            <div className={classes.sideBody} data-empty>
              {k('noEquipment')}
            </div>
          </section>
          <section className={classes.sideCard} data-grow aria-label={k('notes')}>
            <h4>{k('notes')}</h4>
            <div className={classes.sideBody} data-empty={material.notes ? undefined : true}>
              <span className={classes.notes}>{material.notes ?? k('noNotes')}</span>
            </div>
          </section>
        </div>
      </div>
    </Panel>
  );
}
